import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Clock, RotateCcw, Plus, Loader2, GitBranch, UserCog, Trash2, ChevronRight, ShieldCheck, Undo2 } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { EmployeePicker } from "../../components/os/EmployeePicker";
import { useAuth } from "../../context/AuthContext";

const TIPO_LABELS = { permiso: "Permiso", vacaciones: "Vacaciones", gasto: "Gasto", cambio_puesto: "Cambio de puesto", cambio_salario: "Cambio salarial" };
const ESTADO_STYLES = {
  pendiente: "bg-gold/15 text-gold", aprobada: "bg-emerald-500/15 text-emerald-600",
  rechazada: "bg-destructive/15 text-destructive", cambios_solicitados: "bg-sky-500/15 text-sky-600",
};

function EstadoBadge({ e }) {
  const L = { pendiente: "Pendiente", aprobada: "Aprobada", rechazada: "Rechazada", cambios_solicitados: "Cambios solicitados" };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${ESTADO_STYLES[e] || "bg-navy/10 text-navy/50"}`}>{L[e] || e}</span>;
}

function StepTrail({ steps, current }) {
  return (
    <div className="flex flex-col gap-1.5 mt-2">
      {steps.map((s) => {
        const active = s.order === current && s.status === "pendiente";
        const icon = s.status === "aprobado" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          : s.status === "rechazado" ? <XCircle className="w-3.5 h-3.5 text-destructive" />
          : s.status === "omitido" ? <ChevronRight className="w-3.5 h-3.5 text-navy/25" />
          : <Clock className={`w-3.5 h-3.5 ${active ? "text-gold" : "text-navy/25"}`} />;
        return (
          <div key={s.order} className={`flex items-center gap-2 text-xs ${active ? "font-medium text-navy" : "text-navy/55"}`}>
            {icon}
            <span>{s.order + 1}. {s.label}</span>
            <span className="text-navy/35">· {s.approver_name}</span>
            {s.status === "omitido" && <span className="text-navy/30">(omitido{s.skip_reason ? `: ${s.skip_reason}` : ""})</span>}
            {s.comment && <span className="italic text-navy/40">“{s.comment}”</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function Approvals() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const isLeader = meta?.is_leader;

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/approvals/requests?scope=pending").catch(() => ({ data: [] })),
      api.get("/approvals/requests?scope=mine").catch(() => ({ data: [] })),
    ]).then(([p, m]) => { setPending(p.data || []); setMine(m.data || []); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { api.get("/approvals/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (rid, action, comment) => {
    try { await api.post(`/approvals/requests/${rid}/decide`, { action, comment }); toast.success("Registrado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const resubmit = async (rid) => {
    try { await api.post(`/approvals/requests/${rid}/resubmit`); toast.success("Reenviada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <OSLayout title="OMNIA Aprobaciones">
      <div className="max-w-4xl space-y-6" data-testid="approvals-page">
        <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-2">
          {[["pending", "Pendientes de mí", pending.filter((r) => (r.steps.find((s) => s.order === r.current_level) || {}).approver_id === String(user?.id)).length],
            ["mine", "Mis solicitudes"], ["new", "Nueva solicitud"],
            ...(isLeader ? [["routes", "Rutas"], ["deleg", "Delegaciones"]] : [])].map(([k, l, n]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`appr-tab-${k}`}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${tab === k ? "bg-navy text-cream" : "text-navy/55 hover:bg-navy/5"}`}>{l}
              {!!n && <span className="ml-2 text-xs bg-gold text-navy rounded-full px-1.5">{n}</span>}
            </button>
          ))}
        </div>

        {loading && tab !== "new" && tab !== "routes" && tab !== "deleg"
          ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
          : (
          <>
            {tab === "pending" && <RequestList items={pending} onDecide={decide} me={String(user?.id)} mode="approve" />}
            {tab === "mine" && <RequestList items={mine} onResubmit={resubmit} me={String(user?.id)} mode="mine" />}
            {tab === "new" && meta && <NewRequest meta={meta} onDone={() => { setTab("mine"); load(); }} />}
            {tab === "routes" && meta && <Routes meta={meta} />}
            {tab === "deleg" && <Delegations />}
          </>
        )}
      </div>
    </OSLayout>
  );
}

function RequestList({ items, onDecide, onResubmit, me, mode }) {
  const [comment, setComment] = useState({});
  if (!items.length) return <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin solicitudes.</div>;
  return (
    <div className="grid gap-3">
      {items.map((r) => {
        const cur = r.steps.find((s) => s.order === r.current_level);
        const iAmApprover = cur && cur.approver_id === me && r.estado === "pendiente";
        return (
          <div key={r.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`appr-req-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-navy">{r.title}</div>
                <div className="text-xs text-navy/45">{TIPO_LABELS[r.tipo] || r.tipo} · {r.solicitante_name}{r.monto ? ` · $${r.monto.toLocaleString()}` : ""} · {r.policy_name}</div>
              </div>
              <EstadoBadge e={r.estado} />
            </div>
            <StepTrail steps={r.steps} current={r.current_level} />

            {mode === "approve" && iAmApprover && (
              <div className="mt-3 space-y-2 border-t border-navy/5 pt-3">
                <input className="field" placeholder="Comentario (opcional)…" value={comment[r.id] || ""} onChange={(e) => setComment({ ...comment, [r.id]: e.target.value })} data-testid={`appr-comment-${r.id}`} />
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => onDecide(r.id, "aprobar", comment[r.id])} className="btn btn-primary text-sm gap-1" data-testid={`appr-approve-${r.id}`}><CheckCircle2 className="w-4 h-4" /> Aprobar</button>
                  <button onClick={() => onDecide(r.id, "solicitar_cambios", comment[r.id])} className="btn btn-ghost text-sm gap-1" data-testid={`appr-changes-${r.id}`}><Undo2 className="w-4 h-4" /> Solicitar cambios</button>
                  <button onClick={() => onDecide(r.id, "rechazar", comment[r.id])} className="btn btn-ghost text-sm gap-1 text-destructive" data-testid={`appr-reject-${r.id}`}><XCircle className="w-4 h-4" /> Rechazar</button>
                </div>
              </div>
            )}
            {mode === "mine" && r.estado === "cambios_solicitados" && (
              <button onClick={() => onResubmit(r.id)} className="btn btn-primary text-sm gap-1 mt-3" data-testid={`appr-resubmit-${r.id}`}><RotateCcw className="w-4 h-4" /> Reenviar</button>
            )}
            {r.audit?.length > 1 && (
              <details className="mt-2 text-xs text-navy/45"><summary className="cursor-pointer">Bitácora ({r.audit.length})</summary>
                <div className="mt-1 space-y-0.5">{r.audit.map((a, i) => <div key={i}>{a.at?.slice(0, 16).replace("T", " ")} · {a.by_name} · {a.action}{a.comment ? `: ${a.comment}` : ""}</div>)}</div>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}

function NewRequest({ meta, onDone }) {
  const [tipo, setTipo] = useState("permiso");
  const [title, setTitle] = useState("");
  const [monto, setMonto] = useState("");
  const [target, setTarget] = useState(null);
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) return toast.error("Escribe un título/descripción");
    const payload = { ...fields };
    if (["cambio_puesto", "cambio_salario"].includes(tipo)) {
      if (!target) return toast.error("Selecciona al colaborador");
      payload.user_id = target.id;
    }
    setBusy(true);
    try {
      await api.post("/approvals/requests", { tipo, title, payload, monto: monto ? Number(monto) : null });
      toast.success("Solicitud enviada");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-navy/10 rounded-2xl p-6 space-y-4 max-w-lg" data-testid="appr-new">
      <div>
        <label className="text-xs text-navy/60 mb-1 block">Tipo de solicitud</label>
        <select className="field" value={tipo} onChange={(e) => { setTipo(e.target.value); setFields({}); }} data-testid="appr-new-tipo">
          {meta.request_types.map((t) => <option key={t} value={t}>{TIPO_LABELS[t] || t}</option>)}
        </select>
      </div>
      <input className="field" placeholder="Título / descripción" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="appr-new-title" />

      {(tipo === "permiso" || tipo === "vacaciones") && (
        <div className="grid grid-cols-2 gap-3">
          <input type="date" className="field" value={fields.start || ""} onChange={(e) => setFields({ ...fields, start: e.target.value })} data-testid="appr-new-start" />
          <input type="date" className="field" value={fields.end || ""} onChange={(e) => setFields({ ...fields, end: e.target.value })} data-testid="appr-new-end" />
        </div>
      )}
      {tipo === "gasto" && (
        <input type="number" className="field" placeholder="Importe" value={monto} onChange={(e) => setMonto(e.target.value)} data-testid="appr-new-monto" />
      )}
      {tipo === "cambio_puesto" && (
        <div className="space-y-3">
          <div><label className="text-xs text-navy/60 mb-1 block">Colaborador</label><EmployeePicker testid="appr-new-user" value={target?.id} onChange={setTarget} placeholder="¿A quién?" /></div>
          <input className="field" placeholder="Nuevo puesto" value={fields.puesto || ""} onChange={(e) => setFields({ ...fields, puesto: e.target.value })} data-testid="appr-new-puesto" />
          <input className="field" placeholder="Nueva área (opcional)" value={fields.area || ""} onChange={(e) => setFields({ ...fields, area: e.target.value })} />
        </div>
      )}
      {tipo === "cambio_salario" && (
        <div className="space-y-3">
          <div><label className="text-xs text-navy/60 mb-1 block">Colaborador</label><EmployeePicker testid="appr-new-user" value={target?.id} onChange={setTarget} placeholder="¿A quién?" /></div>
          <div className="grid grid-cols-2 gap-3">
            <input type="number" className="field" placeholder="Tarifa por hora" value={fields.tarifa || ""} onChange={(e) => { setFields({ ...fields, tarifa: e.target.value, tipo_tarifa: "por_hora" }); setMonto(e.target.value); }} data-testid="appr-new-tarifa" />
            <input type="date" className="field" value={fields.vigencia_desde || ""} onChange={(e) => setFields({ ...fields, vigencia_desde: e.target.value })} data-testid="appr-new-vigencia" />
          </div>
        </div>
      )}
      <button onClick={submit} disabled={busy} className="btn btn-primary gap-2 w-full" data-testid="appr-new-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Enviar solicitud</button>
    </div>
  );
}

function Routes({ meta }) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { setLoading(true); api.get("/approvals/policies").then((r) => setPolicies(r.data || [])).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  if (loading) return <div className="py-10 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  return (
    <div className="space-y-3" data-testid="appr-routes">
      <p className="text-sm text-navy/50 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Rutas de aprobación configurables por tipo, monto, área y puesto. Motor de niveles dinámico (no fijo).</p>
      {policies.map((p) => (
        <div key={p.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`appr-policy-${p.id}`}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-navy flex items-center gap-2"><GitBranch className="w-4 h-4 text-violet" /> {p.name}</div>
            <span className="text-xs text-navy/40">{TIPO_LABELS[p.tipo] || p.tipo}{!p.active ? " · inactiva" : ""}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs">
            {p.levels.map((l, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <span className="bg-navy/[0.05] rounded px-2 py-0.5 text-navy/70">{i + 1}. {meta.role_labels[l.role_type] || l.role_type}</span>
                {i < p.levels.length - 1 && <ChevronRight className="w-3 h-3 text-navy/25" />}
              </span>
            ))}
          </div>
          {(p.conditions?.min_amount || p.conditions?.max_amount || p.conditions?.area) && (
            <div className="text-[11px] text-navy/40 mt-1.5">Condiciones: {p.conditions.min_amount ? `≥ $${p.conditions.min_amount}` : ""} {p.conditions.max_amount ? `≤ $${p.conditions.max_amount}` : ""} {p.conditions.area ? `área=${p.conditions.area}` : ""}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function Delegations() {
  const [items, setItems] = useState([]);
  const [to, setTo] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const load = useCallback(() => { api.get("/approvals/delegations").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!to || !range.start || !range.end) return toast.error("Completa los campos");
    try { await api.post("/approvals/delegations", { to_user: to.id, start: range.start, end: range.end }); toast.success("Delegación creada"); setTo(null); setRange({ start: "", end: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { try { await api.delete(`/approvals/delegations/${id}`); load(); } catch { toast.error("Error"); } };
  return (
    <div className="space-y-4" data-testid="appr-deleg">
      <div className="bg-white border border-navy/10 rounded-2xl p-4 space-y-3 max-w-lg">
        <div className="text-sm font-medium flex items-center gap-2"><UserCog className="w-4 h-4 text-violet" /> Delegar mis aprobaciones</div>
        <EmployeePicker testid="deleg-user" value={to?.id} onChange={setTo} placeholder="Delegar a…" />
        <div className="grid grid-cols-2 gap-3">
          <input type="date" className="field" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="deleg-start" />
          <input type="date" className="field" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="deleg-end" />
        </div>
        <button onClick={create} className="btn btn-primary text-sm gap-1" data-testid="deleg-create"><Plus className="w-4 h-4" /> Crear delegación</button>
      </div>
      <div className="grid gap-2">
        {items.map((d) => (
          <div key={d.id} className="bg-white border border-navy/10 rounded-xl p-3 flex items-center justify-between text-sm" data-testid={`deleg-${d.id}`}>
            <span className="text-navy">{d.from_name} → {d.to_name} <span className="text-navy/40 text-xs">({d.start} → {d.end})</span></span>
            <button onClick={() => del(d.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
